import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  accountingData: [],
  loading: false,
  error: null,
};

const accountingSlice = createSlice({
  name: "accounting",
  initialState,
  reducers: {
    setAccountingData: (state, action) => {
      state.accountingData = action.payload;
      state.loading = false;
      state.error = null;
    },
    updateAccountingAum: (state, action) => {
      const { isSMA, accountingItem } = action.payload;
      
      if (!accountingItem || !state.accountingData[0]?.funds) {
        toast.error("Failed to update Accounting AUM");
        state.loading = false;
        state.error = null;
        return;
      }

      const { aum } = accountingItem;
      const entityId = isSMA ? accountingItem.sma : accountingItem.fund;
      
      if (!entityId) {
        toast.error("Fund ID not found");
        state.loading = false;
        state.error = null;
        return;
      }

      // Find and update the entity
      const entityToUpdate = state.accountingData[0].funds.find(
        (item) => item.fund === entityId
      );

      if (entityToUpdate) {
        entityToUpdate.aum = aum;
        
        // Only update strategyAUMs for funds, not SMAs
        if (!isSMA && accountingItem.strategyAUMs) {
          entityToUpdate.strategyAUMs = accountingItem.strategyAUMs;
        }
        
        toast.success(`${isSMA ? 'SMA' : 'Fund'} AUM updated successfully`);
      } else {
        toast.error(`${isSMA ? 'SMA' : 'Fund'} not found in accounting data`);
      }
      
      state.loading = false;
      state.error = null;
    },
    addAccountingAum: (state, action) => {
      const { isSMA, accountingItem } = action.payload;
      
      if (!accountingItem || !state.accountingData[0]?.funds) {
        toast.error("Failed to add Accounting AUM");
        state.loading = false;
        state.error = null;
        return;
      }

      const { accountingId, aum, date, managerId } = accountingItem;
      const entityId = isSMA ? accountingItem.sma : accountingItem.fund;
      
      if (!entityId) {
        toast.error("Fund ID not found");
        state.loading = false;
        state.error = null;
        return;
      }

      // Check if entity already exists
      const existingEntity = state.accountingData[0].funds.find(
        (item) => item.fund === entityId
      );

      if (existingEntity) {
        // Update existing entity's AUM
        existingEntity.aum = aum;
        toast.success(`${isSMA ? 'SMA' : 'Fund'} AUM updated successfully`);
      } else {
        // Add new entity
        const newEntity = {
          fund: entityId,
          aum: aum,
          managerId: managerId,
          accountingId: accountingId,
          date: date,
          isSMA: isSMA
        };

        // Only add strategyAUMs for funds, not SMAs
        if (!isSMA) {
          newEntity.strategyAUMs = [];
        }

        state.accountingData[0].funds.push(newEntity);
        toast.success(`${isSMA ? 'SMA' : 'Fund'} added successfully`);
      }

      state.loading = false;
      state.error = null;
    },
  
    setAccountingLoading: (state, action) => {
      state.loading = action.payload;
    },
    setAccountingError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearAccountingError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setAccountingData,
  updateAccountingAum,
  addAccountingAum,
  setAccountingLoading,
  setAccountingError,
  clearAccountingError,
} = accountingSlice.actions;

// Selectors - Direct access to accounting state
export const selectAccountingData = (state) => state.accounting?.accountingData || [];
export const selectAccountingLoading = (state) => state.accounting?.loading || false;
export const selectAccountingError = (state) => state.accounting?.error || null;

export default accountingSlice.reducer;